import { Controller, UseFormReturn } from 'react-hook-form'

import { StateParams, Textfield } from '@clientbase/clientbase-library'
import { Box, Stack, Typography } from '@mui/material'

import InputController from 'components/InputController'
import { PhoneInput } from 'components/PhoneInput'
import StateAutocomplete from 'components/StateAutocomplete'

import { Debouncer, onlyNumber, UFOptions } from 'utils'
import { getCEPData } from 'utils/cepService'

import { CustomerFormData } from './customerSchema'

type CustomerFormProps = {
  form: UseFormReturn<CustomerFormData>
}

// O PhoneInput fica ao lado de Textfields, então usa o `compact` (36px, 14px e a
// mesma borda deles) e só ajusta a cor do texto.
const PHONE_INPUT_STYLE = { color: '#3F403F' }

// Dados de quem paga a mensalidade. Vêm pré-preenchidos com o cadastro da conta;
// o merchant só completa o que faltar (o endereço é exigido pela nota fiscal).
// Mesmos componentes do cadastro de cliente: telefone com país, CEP que preenche
// o endereço e UF em lista.
const CustomerForm = ({ form }: CustomerFormProps) => {
  const { control, setValue, setError, clearErrors, formState } = form

  // Debouncer é um hook (guarda o timeout num useRef): precisa ser chamado no
  // topo do componente, nunca dentro de useMemo, ou o React perde a contagem de
  // hooks na renderização seguinte e a tela quebra.
  const fillAddress = Debouncer(async (cep: string) => {
    const response = await getCEPData(cep)
    if (response.error || (!response.localidade && !response.logradouro)) {
      setError('postal_code', {
        message: response.message || 'CEP não encontrado'
      })
      return
    }

    const options = { shouldValidate: true, shouldDirty: true }
    if (response.logradouro) setValue('street', response.logradouro, options)
    if (response.bairro) setValue('neighborhood', response.bairro, options)
    if (response.localidade) setValue('city', response.localidade, options)
    if (response.uf) setValue('state', response.uf, options)
    clearErrors('postal_code')
  }, 800)

  return (
    <Stack gap={2}>
      <Typography variant="smLight" component="p" m={0} color="#646664">
        Esses dados aparecem na sua fatura e na nota fiscal da mensalidade.
      </Typography>

      <InputController control={control} name="name">
        <Textfield fullWidth label="Nome ou razão social" />
      </InputController>
      <Box
        display="grid"
        gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }}
        gap={1.5}
        alignItems="start"
      >
        <InputController control={control} name="document">
          <Textfield fullWidth label="CPF/CNPJ" mask="cpfCnpj" />
        </InputController>
        <InputController control={control} name="phone">
          <PhoneInput
            compact
            country="br"
            label="Telefone"
            errorLabel={formState.errors.phone?.message || ''}
            inputStyle={PHONE_INPUT_STYLE}
          />
        </InputController>
      </Box>
      <InputController control={control} name="email">
        <Textfield fullWidth label="E-mail de cobrança" type="email" />
      </InputController>

      <Typography variant="smBold" component="p" m={0} mt={1}>
        Endereço
      </Typography>
      <Box
        display="grid"
        gridTemplateColumns={{ xs: '1fr', sm: '1fr 2fr' }}
        gap={1.5}
        alignItems="start"
      >
        <Controller
          name="postal_code"
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Textfield
              fullWidth
              label="CEP"
              mask="cep"
              value={value}
              onChange={(e) => {
                const next = e.target.value
                onChange(next)
                if (onlyNumber(next).length === 8) fillAddress(onlyNumber(next))
              }}
              helperText={error?.message}
              error={!!error}
            />
          )}
        />
        <InputController control={control} name="street">
          <Textfield fullWidth label="Rua" />
        </InputController>
      </Box>
      <Box
        display="grid"
        gridTemplateColumns={{ xs: '1fr', sm: '1fr 2fr' }}
        gap={1.5}
        alignItems="start"
      >
        <InputController control={control} name="number">
          <Textfield fullWidth label="Número" />
        </InputController>
        <InputController control={control} name="complement">
          <Textfield fullWidth label="Complemento" />
        </InputController>
      </Box>
      <InputController control={control} name="neighborhood">
        <Textfield fullWidth label="Bairro" />
      </InputController>
      <Box
        display="grid"
        gridTemplateColumns={{ xs: '1fr', sm: '2fr 1fr' }}
        gap={1.5}
        alignItems="start"
      >
        <InputController control={control} name="city">
          <Textfield fullWidth label="Cidade" />
        </InputController>
        <Controller
          name="state"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <StateAutocomplete
              error={error?.message}
              label={<Typography variant="smLight">Estado</Typography>}
              setStateError={() => null}
              // O X do select manda null: limpa o campo em vez de quebrar.
              setStateValue={(state: StateParams | null) =>
                setValue('state', state?.value ?? '', {
                  shouldValidate: true,
                  shouldDirty: true
                })
              }
              stateValue={
                {
                  label:
                    UFOptions.find((option) => option.value === field.value)
                      ?.label ?? '',
                  value: field.value ?? ''
                } as StateParams
              }
            />
          )}
        />
      </Box>
    </Stack>
  )
}

export default CustomerForm
